/** The catalog read takes about 1 s, so a date range keeps its catalog for 2 minutes instead of rereading it. */
export const CATALOG_STALE_TIME_MS = 2 * 60 * 1000;

export const SHIFT_REPORT_QUERY_KEY = "shift-report";
export const SHIFT_REPORT_COLUMNS_QUERY_KEY = "shift-report-columns";
export const SHIFT_REPORT_LIST_QUERY_KEY = "shift-report-list";
/** A saved-report change the API refused without a key the page shows. */
export const SAVED_REPORT_ERROR_KEY = "shiftReport.reports.saveError";
/** The API's refusal of a change to a report someone else changed since it was read. */
export const REPORT_CHANGED_KEY = "shift_report.report.changed";
/** A saved report's name, as the API caps it. */
export const REPORT_NAME_MAX_LENGTH = 40;

/** The Shifts count's column id (the API's `ROWS_COLUMN`). */
export const ROWS_COLUMN = "__rows";
/** `flattenTree` path of the total; a child's path is its index chain from the total down (`0-3-1`). */
export const TOTAL_PATH = "total";
export const PATH_SEPARATOR = "-";
/** The page offers three grouping levels; the API itself has no limit (N9). */
export const MAX_GROUP_LEVELS = 3;
/** Error keys the page shows as they are; any other failure shows `FETCH_ERROR_KEY`. */
export const SHOWN_ERROR_KEY_PREFIXES = ["shift_report.", "error."] as const;
export const FETCH_ERROR_KEY = "shiftReport.fetchError";
/** What a failed Staffing and Punch Summary download shows when the server sent no key the page shows. */
export const PUNCH_SUMMARY_ERROR_KEY = "shiftReport.punchSummary.error";

export const ROW_MODES = ["joined", "scheduled_only", "actual_only"] as const;
export const DATE_ATTRIBUTIONS = ["scheduled_first", "per_side"] as const;
export const REMOVAL_CLASSES = ["call_in", "facility_cancel", "pto", "other_removal", "unknown"] as const;
export const OT_MODES = ["stored", "trigger"] as const;
export const GROUPED_TEXTS = ["last", "values"] as const;
export const RATE_BASES = ["base", "effective"] as const;
export const SHIFT_HOURS_MODES = ["scale", "whole"] as const;
export const FILLABLE_COLUMNS = ["date", "category"] as const;
export const COMPARISON_OPS = ["lt", "le", "gt", "ge", "eq", "ne", "is_blank", "not_blank"] as const;
export const AGGREGATIONS = ["sum", "mean", "count", "unique", "sum_days", "mean_days", "last", "values", "primary_job"] as const;
export const COLUMN_SECTIONS = ["identity", "schedule", "punches", "variance", "exceptions", "split_and_dating", "pay", "day_metrics"] as const;
export const COLUMN_SIDES = ["scheduled", "actual", "row", "identity", "view"] as const;
export const COLUMN_KINDS = ["number", "text", "bool", "date", "datetime", "day_metric"] as const;
export const TIME_OF_DAY_SIDES = ["scheduled", "actual"] as const;
/** The Labor report's id of the Shifts count measure (`__rows`); every other measure id is `<column>_<agg>`. */
export const SHIFTS_MEASURE_ID = "shifts";
/** The page re-runs the report this long after the last change, so a burst of clicks sends one request. */
export const REPORT_DEBOUNCE_MS = 1000;
/** Late In, Came Early and Left Early at or past this many minutes read in error ink. */
export const LATE_MINUTES_MARK = 15;
/** Ran Over at or past this many minutes reads in error ink. */
export const RAN_OVER_MINUTES_MARK = 30;
/** A Lunch Duration over this many minutes is tagged. */
export const LONG_LUNCH_MINUTES = 60;
/** The overtime hours and cost columns (both sides): a value above 0 is tagged. */
export const OVERTIME_COLUMNS: ReadonlySet<string> = new Set(
	["hours_daily_ot", "hours_weekly_ot", "hours_consecutive_ot", "total_ot_hours", "ot_cost"].flatMap((suffix) => [`scheduled_${suffix}`, `actual_${suffix}`]),
);
/** Minute columns read in error ink at or past their mark. */
export const MINUTES_MARKS: Readonly<Record<string, number>> = {
	late_in_minutes: LATE_MINUTES_MARK,
	came_early_minutes: LATE_MINUTES_MARK,
	left_early_minutes: LATE_MINUTES_MARK,
	ran_over_minutes: RAN_OVER_MINUTES_MARK,
};
export const LUNCH_DURATION_COLUMN = "punched_break_minutes";
export const SHIFT_DELTA_COLUMN = "shift_delta_minutes";
export const ROLE_COLUMN = "role";
export const AGENCY_NAME_COLUMN = "agency_name";
/** The longest custom period, both ends counted: inside the API's one-year limit (C32) for every start date. */
export const MAX_CUSTOM_RANGE_DAYS = 365;
/** Excel refuses a worksheet name longer than this. */
export const EXCEL_SHEET_NAME_LENGTH = 31;
/** The columns the Summary's level picker lists first, under "Common", in this order. */
export const COMMON_GROUP_COLUMNS = ["role", "job", "name", "date", "shift_type", "unit", "employment_type", "agency_name", "punch_status"] as const;
/**
 * The filter rail's sections, in the prototype's rail order; clicking a cell adds its column after these. Total OT is a
 * banded number column: the catalog lists its hour bands then its blank label, so its options are those bands.
 */
export const FILTER_RAIL_COLUMNS = [
	"role",
	"job",
	"date",
	"shift_type",
	"unit",
	"employment_type",
	"punch_status",
	"excluded_from_ppd",
	"actual_total_ot_hours",
	"call_in",
] as const;
/** The Columns picker leaves out the day metrics (one value per day, not per shift) … */
export const DAY_METRICS_SECTION = "day_metrics";
/** … and the trigger columns, which stay blank under the page's `ot_mode: stored`. */
export const TRIGGER_COLUMN_PATTERN = /^(scheduled|actual)_trigger_/;
/** A Yes/No column's filter keys (the API's `yes_no`). */
export const BOOL_FILTER_KEYS = { yes: "Yes", no: "No" } as const;
/** The column whose cells and options are people: its filter key is the nurse id. */
export const NAME_COLUMN = "name";
/** The columns whose cells show a job's display name while their filter key is the job id (the API's `JOB_COLUMNS`). */
export const JOB_COLUMNS: ReadonlySet<string> = new Set(["job", "scheduled_job", "actual_job"]);
/** The API's hour bands and the page's clock times read on a 12-hour clock with a/p ("7a", "6:30p"). */
export const HOURS_PER_HALF_DAY = 12;
/** Joins the grouping levels in the table's first header and the export's sheet names ("Date › Role"). */
export const LEVEL_SEPARATOR = " › ";

/** `data-testid` stems (spec section 8, Test ids); lists append `-{id}` / `-{index}`. */
export const TEST_IDS = {
	page: "shift-report-page",
	period: "shift-report-period",
	reports: "shift-report-reports",
	/** The page while the department's report list loads. */
	reportsLoading: "shift-report-reports-loading",
	/** The Reports menu's commands; a custom report's settings gear (rename or delete) is `reportsManage-{id}`. */
	reportsNew: "shift-report-reports-new",
	reportsSaveAs: "shift-report-reports-save-as",
	reportsSaveChanges: "shift-report-reports-save-changes",
	reportsManage: "shift-report-reports-manage",
	/** The pin strip; a card is `-card-{key}`, its unpin `-card-{key}-unpin`; Pin a report is `-add`. */
	pinStrip: "shift-report-pins",
	/** The new report snackbar's Pin it. */
	pinIt: "shift-report-pin-it",
	/** The report dialog; its name field is `-name`, its Delete report `-delete`. */
	reportDialog: "shift-report-report-dialog",
	deleteDialog: "shift-report-delete-dialog",
	discardDialog: "shift-report-discard-dialog",
	/** The rail's facet sections are `shift-report-filters-{column}` (+ `-option-{slug}`). */
	filters: "shift-report-filters",
	/** The rail itself; its Clear all is `-clear`, its collapse `-collapse`. */
	filtersSidebar: "shift-report-filters-sidebar",
	/** The toolbar's "Filters (N)" button that reopens a collapsed rail. */
	filtersOpen: "shift-report-filters-open",
	expandAll: "shift-report-expand-all",
	collapseAll: "shift-report-collapse-all",
	rowCount: "shift-report-row-count",
	columns: "shift-report-columns",
	summaryButton: "shift-report-summary-button",
	summaryPopover: "shift-report-summary-popover",
	/** Levels are `-{index}-up` / `-{index}-remove`. */
	summaryLevel: "shift-report-summary-level",
	summaryAddLevel: "shift-report-summary-add-level",
	/** Measures are `-{id}-agg` / `-{id}-remove`. */
	summaryMeasure: "shift-report-summary-measure",
	summaryAddMeasures: "shift-report-summary-add-measures",
	/** Each Add measures row's info glyph, `-{columnId}` appended. */
	summaryMeasureInfo: "shift-report-measure-info",
	summaryRemove: "shift-report-summary-remove",
	export: "shift-report-export",
	punchSummaryDownloadButton: "shift-report-punch-summary-download-button",
	table: "shift-report-table",
	updating: "shift-report-updating",
	empty: "shift-report-empty",
	error: "shift-report-error",
	retry: "shift-report-retry",
	/** The grouped grid; `SystemDataGrid` adds `-grid` / `-root`. */
	tree: "shift-report-tree",
	/** A group row is `-{path}`, its chevron `-{path}-toggle`. */
	node: "shift-report-node",
	/** A leaf's shift table is `-{path}`. */
	leaf: "shift-report-leaf",
	flatGrid: "shift-report-flat-grid",
	/** A shift-row cell is `-{rowId}-{column}`. */
	detail: "shift-report-detail",
	menuItem: "admin-menu-shift-report",
	betaBadge: "shift-report-beta-badge",
} as const;
